import { UAParser } from 'ua-parser-js'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { urlRegex } from './regex'
import { Toast } from 'antd-mobile'
import clsx, { ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export const SENSORS_ANONYMOUS_ID_COOKIE_KEY = 'mespery_anonymous_id'
const ANONYMOUS_ID_COOKIE_MAX_AGE_DAYS = 365
const ANON_ID_SEARCH_PARAM_KEY = 'anon_id'
const PACKAGE_UUID_COOKIE_KEY = 'pkg_uuid_val'

export const formatSpaces = (text: string) => {
  return text.replace(/\n/g, '<br/>').replace(/[\u0020\u3000]{2,}/g, (m) => '\u00A0'.repeat(m.length))
}

export const getGlobalConfig = () => {
  const ENV = import.meta.env
  const {
    VITE_GLOB_API_URL,
    VITE_ENVIRONMENT,
    VITE_SENSORS_TRACK_URL,
    VITE_IM_API_URL,
    VITE_GOOGLE_CLIENT_ID,
    VITE_WRITING_STATION_URL,
    VITE_FACEBOOK_APP_ID,
    VITE_MAIN_URL,
    VITE_TASK_API_URL,
    VITE_WS_ENDPOINT,
    VITE_CMTY_API_URL,
    VITE_IM_WEB_URL,
  } = ENV

  return {
    VITE_GLOB_API_URL,
    VITE_ENVIRONMENT,
    VITE_SENSORS_TRACK_URL,
    VITE_IM_API_URL,
    VITE_GOOGLE_CLIENT_ID,
    VITE_WRITING_STATION_URL,
    VITE_FACEBOOK_APP_ID,
    VITE_MAIN_URL,
    VITE_TASK_API_URL,
    VITE_WS_ENDPOINT,
    VITE_CMTY_API_URL,
    VITE_IM_WEB_URL,
  }
}

export const initVConsole = async () => {
  const REACT_APP_ENV = getGlobalConfig().VITE_ENVIRONMENT
  // 检查是否是测试或预发环境
  if (['dev', 'test', 'pre'].includes(REACT_APP_ENV)) {
    try {
      const { default: VConsole } = await import('vconsole')
      new VConsole()
    } catch (error) {
      console.error('Failed to initialize vConsole:', error)
    }
  }
}

export const isPWA = () => {
  // iOS 检测
  const isIOSPWA = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return ['standalone', 'fullscreen'].some((mode) => window.navigator[mode])
  }

  // Android 检测
  const isAndroidPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches
  }

  // 组合所有检测方法
  return isIOSPWA() || isAndroidPWA()
}

// 判断是否为iOS设备
function isIOS() {
  return /\biPhone|iPod|iPad\b/i.test(navigator.userAgent)
}

/**
 * 移动端时返回true
 * @returns {Boolean}
 */
export const isMobile = (): boolean => {
  const { getDevice } = new UAParser()
  const device = getDevice()

  if (window.matchMedia('(max-width: 768px)').matches) {
    return true
  } else if (device.type === 'mobile' || device.type === 'tablet') {
    if (window.innerWidth > 768) {
      return false
    }
    return true
  } else {
    return false
  }
}

export const checkSafari = () => {
  const parser = new UAParser()
  const browser = parser.getBrowser()
  return browser.name === 'Safari' || browser.name === 'Mobile Safari'
}

export const saveCurrentTimestamp = () => {
  const currentTimestamp = dayjs().valueOf() // 获取当前时间的时间戳

  try {
    window?.localStorage?.setItem('timestamp', String(currentTimestamp)) // 保存到 localStorage
  } catch (e) {
    console.error('timestamp Storage error:', e)
  }
}

/**
 * 获取cookie
 * @param arg key
 * @returns
 */
const normalizeAnonymousId = (value?: string | null) => {
  const trimmedValue = value?.trim()
  return trimmedValue || undefined
}

const createAnonymousId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomValue = Math.floor(Math.random() * 16)
    const value = char === 'x' ? randomValue : (randomValue & 0x3) | 0x8
    return value.toString(16)
  })
}

const getAnonymousCookieOptions = (): Cookies.CookieAttributes => {
  const hostname = window.location.hostname.toLowerCase()
  const options: Cookies.CookieAttributes = {
    expires: ANONYMOUS_ID_COOKIE_MAX_AGE_DAYS,
    path: '/',
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  }

  if (hostname === 'mespery.com' || hostname.endsWith('.mespery.com')) {
    options.domain = '.mespery.com'
  }

  return options
}

export const setSensorsAnonymousId = (anonymousId?: string | null) => {
  const normalizedAnonymousId = normalizeAnonymousId(anonymousId)
  if (!normalizedAnonymousId) {
    return undefined
  }

  try {
    Cookies.set(SENSORS_ANONYMOUS_ID_COOKIE_KEY, normalizedAnonymousId, getAnonymousCookieOptions())
  } catch (e) {
    console.error('set anonymous id cookie error:', e)
  }

  return normalizedAnonymousId
}

export const getSensorsAnonymousId = () => {
  return normalizeAnonymousId(Cookies.get(SENSORS_ANONYMOUS_ID_COOKIE_KEY))
}

export const getOrCreateSensorsAnonymousId = () => {
  const anonymousId =
    normalizeAnonymousId(new URLSearchParams(window.location.search).get(ANON_ID_SEARCH_PARAM_KEY)) ||
    normalizeAnonymousId(Cookies.get(PACKAGE_UUID_COOKIE_KEY)) ||
    getSensorsAnonymousId() ||
    createAnonymousId()

  return setSensorsAnonymousId(anonymousId)
}

export const getCookieAuthInfo = <T extends string[]>(...arg: T) => {
  try {
    const result = {} as { [K in T[number]]?: string }
    arg.forEach((key) => {
      result[key as T[number]] = Cookies.get(key) || undefined
    })

    return result
  } catch (e) {
    console.error('get cookie or storage error:', e)
    return {} as { [K in T[number]]?: string }
  }
}

/**
 * 设置cookie
 * @param key
 * @param v
 * @param {Cookies.CookieAttributes} cookieOptions cookie配置
 */
export const setCookieAuthInfo = (key: string, v: string, cookieOptions?: Cookies.CookieAttributes) => {
  let value = v
  if (typeof value !== 'string') {
    value = JSON.stringify(value)
  }

  try {
    Cookies.set(key, value, {
      domain: getGlobalConfig().VITE_ENVIRONMENT === 'dev' ? 'localhost' : '.mespery.com',
      ...cookieOptions,
    })
  } catch (e) {
    console.error('set cookie or storage error:', e)
  }
}

/**
 * 移除cookie
 * @param key
 * @param {Cookies.CookieAttributes} cookieOptions cookie配置
 */
export const removeCookieAuthInfo = (key: string, cookieOptions?: Cookies.CookieAttributes) => {
  try {
    Cookies.remove(key, {
      domain: getGlobalConfig().VITE_ENVIRONMENT === 'dev' ? 'localhost' : '.mespery.com',
      ...cookieOptions,
    })
  } catch (e) {
    console.error('remove cookie or storage error:', e)
  }
}

/**
 * 获取主站域名
 * @returns
 */
export const getMainDomain = () => {
  return getGlobalConfig().VITE_MAIN_URL
}

/**
 * 判断是否在iframe内
 * @returns
 */
export const isInIframe = () => {
  try {
    // 方法1：比较 self 和 top
    if (window.self !== window.top) {
      return true
    }

    // 方法2：检查 frameElement
    if (window.frameElement) {
      return true
    }

    // 方法3：检查 parent 是否相同
    if (window.parent !== window) {
      return true
    }

    return false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // 如果出现安全错误，通常意味着在跨域 iframe 中
    if (error.name === 'SecurityError' || error.name === 'DOMException') {
      return true
    }
    return false
  }
}

/**
 * 跳转主站登录
 */
export const jumpMainLogin = (originURL?: string) => {
  Cookies.set('redirectUrl', originURL || window.location.href, {
    domain: getGlobalConfig().VITE_ENVIRONMENT === 'dev' ? 'localhost' : '.mespery.com',
  })

  customJumpHref(`/login`)
}

/**
 * 全局自定义跳转方法
 * iframe情况下通过postMessage通信父页面跳转
 * 非iframe情况下通过location href跳转
 * @param url 路由地址
 */
export const customJumpHref = async (url: string) => {
  if (typeof url !== 'string') {
    console.error('url type is not string')
    return
  }

  let href = url

  if (!href.match(urlRegex)) {
    if (href[0] !== '/') {
      href = `/${href.slice(0)}`
    }

    const { token } = getCookieAuthInfo('token')
    if (href.includes('/message') || href.includes('/share')) {
      const { VITE_IM_WEB_URL } = getGlobalConfig()
      if (token) {
        setCookieAuthInfo('token', token)
      } else {
        await new Promise<void>((resloveFn) => {
          Toast.show({
            position: 'top',
            content: 'Login to continue',
            afterClose: resloveFn,
            duration: 1000,
          })
        })

        return jumpMainLogin(VITE_IM_WEB_URL)
      }

      href = `${VITE_IM_WEB_URL}${href}`
    } else {
      href = `${getMainDomain()}${href}`
    }
  }

  if (isInIframe()) {
    // iframe内
    try {
      window.top?.postMessage(
        {
          type: 'location',
          value: href,
        },
        document.referrer,
      )
    } catch (error) {
      console.error('postMessage error:', error)
    }
  } else {
    if (isIOS()) {
      // ios在页面状态还未完成加载或者只是可交互态时发生跳转行为，
      // 可能会被判定为跳转前的页面为中间态然后导致页面不会被推进浏览器历史堆栈
      // 用settimeout将跳转行为放进下一个宏任务队列
      setTimeout(() => {
        window.location.href = href
      }, 0)
      return
    }
    window.location.href = href
  }
}

export const transformContent = (content: string) => {
  const html = content
    .split('\n')
    .map((item) => {
      return `<p>${item}</p>`
    })
    .join('')

  return {
    __html: html,
  }
}

/**
 * twMerge、clsx类名合并
 * @param {ClassValue[]} args
 * @returns {string}
 */
export const cn = (...args: ClassValue[]): string => {
  return twMerge(clsx(args))
}
